import { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext.jsx';
import { api } from '../lib/api.js';
import { formatMoney } from '../lib/format.js';
import { cx, formatTime, safeParse, shiftBreakdown } from '../lib/util.js';
import { Btn, MonoInput, PageHeading, Tag, Field } from '../components/ui.jsx';
import { Modal } from '../components/Modal.jsx';
import { PinGate } from '../components/PinGate.jsx';

const TABS = [
  { key: 'stock', label: 'Target Stock' },
  { key: 'items', label: 'Item Management' },
  { key: 'shifts', label: 'Sales Ledger' },
  { key: 'audits', label: 'Audit History' },
];

/** Catches 401s from expired/revoked manager sessions and re-locks the portal. */
function useRequireManager() {
  const { setToken, pushToast } = useApp();
  return (err) => {
    if (err?.status === 401) {
      setToken('');
      pushToast('Manager session expired — re-enter PIN', 'ember');
    } else {
      pushToast(err?.message || 'Request failed', 'ember');
    }
    return err?.status === 401;
  };
}

export default function ManagerPortal() {
  const { managerAuthed } = useApp();
  const [gateOpen, setGateOpen] = useState(!managerAuthed);
  const [tab, setTab] = useState('stock');

  if (!managerAuthed) {
    return (
      <div className="mx-auto max-w-md">
        <PageHeading eyebrow="Restricted" title="Manager Portal" />
        <div className="border border-ember/50 bg-surface p-8 text-center shadow-neon-ember">
          <p className="font-mono text-[11px] uppercase tracking-widest text-mist">
            A manager PIN is required to view target stock, manage items, and
            read the sales &amp; audit ledgers.
          </p>
          <Btn
            variant="ember"
            size="lg"
            className="mt-6 w-full"
            onClick={() => setGateOpen(true)}
          >
            Unlock Manager Portal
          </Btn>
        </div>
        <PinGate open={gateOpen} onCancel={() => setGateOpen(false)} title="Manager Access" />
      </div>
    );
  }

  return (
    <div>
      <PageHeading
        eyebrow="Restricted Access"
        title="Manager Portal"
        live
        actions={<Tag tone="ember">Manager</Tag>}
      />

      <div className="mb-6 flex flex-wrap gap-1 border-b border-edge">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cx(
              '-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors',
              tab === t.key
                ? 'border-neon text-neon'
                : 'border-transparent text-mist hover:text-snow'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'stock' && <StockHub />}
      {tab === 'items' && <ItemManager />}
      {tab === 'shifts' && <ShiftLedger />}
      {tab === 'audits' && <AuditHistory />}
    </div>
  );
}

function StockHub() {
  const { snapshot, token, pushToast, refresh } = useApp();
  const guard = useRequireManager();
  const items = snapshot.items || [];
  const [targets, setTargets] = useState({});
  const [busyId, setBusyId] = useState(null);

  const saveTarget = async (it) => {
    const val = targets[it.id];
    if (val == null || val === '') return;
    setBusyId(`target-${it.id}`);
    try {
      const res = await api.updateItem(it.id, { target_stock: val }, token);
      pushToast(`Target for ${res.name} set to ${res.target_stock}`);
      refresh();
    } catch (err) {
      guard(err);
    } finally {
      setBusyId(null);
    }
  };

  const restock = async (it) => {
    setBusyId(`restock-${it.id}`);
    try {
      await api.restock(it.id, token);
      pushToast(`Restocked ${it.name} to target`);
      refresh();
    } catch (err) {
      guard(err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3">
      {items.map((it) => {
        const delta = it.target_stock - it.current_stock;
        const val = targets[it.id];
        const canRestock = it.target_stock > 0 && delta !== 0;
        return (
          <div
            key={it.id}
            className="flex flex-wrap items-center justify-between gap-3 border border-edge bg-panel p-4"
          >
            <div className="min-w-0">
              <p className="font-mono text-sm font-bold text-snow">{it.name}</p>
              <p className="font-mono text-xs text-dim">
                Current{' '}
                <span className={it.current_stock > 0 ? 'text-neon' : 'text-ember'}>
                  {it.current_stock}
                </span>
                <span className="mx-2 text-edge">/</span>
                Target <span className="text-mist">{it.target_stock}</span>
                {delta !== 0 && (
                  <span className="ml-2 text-dim">
                    ({delta > 0 ? `+${delta} to add` : `${-delta} over target`})
                  </span>
                )}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="number"
                min="0"
                value={val ?? it.target_stock}
                onChange={(e) =>
                  setTargets((p) => ({
                    ...p,
                    [it.id]:
                      e.target.value === ''
                        ? ''
                        : Math.max(0, Math.floor(Number(e.target.value) || 0)),
                  }))
                }
                className="tnum w-24 border border-edge bg-void px-3 py-2 text-right font-mono text-sm text-snow outline-none focus:border-neon"
              />
              <Btn
                size="sm"
                variant="ghost"
                disabled={val == null || val === '' || busyId === `target-${it.id}`}
                onClick={() => saveTarget(it)}
              >
                {busyId === `target-${it.id}` ? 'Saving…' : 'Set Target'}
              </Btn>
              <Btn
                size="sm"
                variant="teal"
                disabled={!canRestock || busyId === `restock-${it.id}`}
                onClick={() => restock(it)}
              >
                {busyId === `restock-${it.id}` ? 'Restocking…' : '+ Restock to Target'}
              </Btn>
            </div>
          </div>
        );
      })}
    </div>
  );
}
function ItemManager() {
  const { snapshot, token, pushToast, refresh } = useApp();
  const guard = useRequireManager();
  const items = snapshot.items || [];

  const [addOpen, setAddOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: '', payout_rate: '', target_stock: 0 });
  const [busy, setBusy] = useState(false);

  const openAdd = () => {
    setEdit(null);
    setForm({ name: '', payout_rate: '', target_stock: 0 });
    setAddOpen(true);
  };

  const openEdit = (it) => {
    setEdit(it);
    setForm({ name: it.name, payout_rate: it.payout_rate, target_stock: it.target_stock });
    setAddOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || form.payout_rate === '' || busy) return;
    setBusy(true);
    const payload = {
      name: form.name.trim(),
      payout_rate: Number(form.payout_rate),
      target_stock: Math.max(0, Math.floor(Number(form.target_stock) || 0)),
    };
    try {
      if (edit) {
        await api.updateItem(edit.id, payload, token);
        pushToast('Item updated');
      } else {
        await api.addItem(payload, token);
        pushToast('Item added');
      }
      setAddOpen(false);
      refresh();
    } catch (err) {
      guard(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Soft-delete "${it.name}"? It stays in ledgers but leaves the register.`)) {
      return;
    }
    try {
      await api.deleteItem(it.id, token);
      pushToast(`${it.name} removed`, 'ember');
      refresh();
    } catch (err) {
      guard(err);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[11px] uppercase tracking-widest text-dim">
          Add new items, edit payout rates, or remove outdated ones
        </p>
        <Btn variant="teal" onClick={openAdd}>
          + Add Item
        </Btn>
      </div>

      <div className="overflow-x-auto border border-edge bg-panel">
        <table className="w-full min-w-[680px] border-collapse text-left">
          <thead>
            <tr className="border-b border-edge font-mono text-[10px] uppercase tracking-widest text-dim">
              <th className="px-4 py-3">Item</th>
              <th className="px-4 py-3">Payout rate</th>
              <th className="px-4 py-3">Target stock</th>
              <th className="px-4 py-3">Current</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-edge/60 last:border-0">
                <td className="px-4 py-3 font-mono text-sm font-bold text-snow">{it.name}</td>
                <td className="px-4 py-3 font-mono text-sm text-neon">
                  {formatMoney(it.payout_rate)}
                </td>
                <td className="px-4 py-3 font-mono text-sm text-mist">{it.target_stock}</td>
                <td
                  className={`px-4 py-3 font-mono text-sm ${
                    it.current_stock > 0 ? 'text-snow' : 'text-ember'
                  }`}
                >
                  {it.current_stock}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Btn size="xs" variant="ghost" onClick={() => openEdit(it)}>
                      Edit
                    </Btn>
                    <Btn size="xs" variant="ember-outline" onClick={() => remove(it)}>
                      Remove
                    </Btn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title={edit ? 'Edit Item' : 'Add New Item'}
        accent="teal"
        footer={
          <>
            <Btn variant="ghost" onClick={() => setAddOpen(false)}>
              Cancel
            </Btn>
            <Btn
              type="submit"
              form="item-form"
              variant="teal"
              disabled={!form.name.trim() || form.payout_rate === '' || busy}
            >
              {busy ? 'Saving…' : edit ? 'Save Changes' : 'Add Item'}
            </Btn>
          </>
        }
      >
        <form id="item-form" onSubmit={save} className="space-y-4">
          <Field label="Item name">
            <MonoInput
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. NOS Energy Drink"
              maxLength={40}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Payout rate ($ per item)">
              <MonoInput
                type="number"
                min="0"
                step="0.01"
                value={form.payout_rate}
                onChange={(e) => setForm({ ...form, payout_rate: e.target.value })}
                placeholder="800"
              />
            </Field>
            <Field label="Initial target stock">
              <MonoInput
                type="number"
                min="0"
                value={form.target_stock}
                onChange={(e) =>
                  setForm({
                    ...form,
                    target_stock:
                      e.target.value === ''
                        ? ''
                        : Math.max(0, Math.floor(Number(e.target.value) || 0)),
                  })
                }
                placeholder="20"
              />
            </Field>
          </div>
        </form>
      </Modal>
    </div>
  );
}
function ShiftLedger() {
  const { snapshot, token, pushToast, refresh } = useApp();
  const guard = useRequireManager();
  const logs = snapshot.shiftLogs || [];
  const [nameFilter, setNameFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(null);

  const handleDelete = async (l) => {
    if (!window.confirm(`Delete shift from ${l.employee_name} (${formatTime(l.timestamp)})? This only removes the ledger entry.`)) return;
    setBusy(l.id);
    try {
      await api.deleteShift(l.id, token);
      pushToast('Shift removed from ledger');
      refresh();
    } catch (err) {
      guard(err);
    } finally {
      setBusy(null);
    }
  };

  const filtered = useMemo(
    () =>
      logs.filter((l) => {
        if (
          nameFilter &&
          !l.employee_name.toLowerCase().includes(nameFilter.toLowerCase())
        ) {
          return false;
        }
        const ts = new Date(l.timestamp).getTime();
        if (Number.isNaN(ts)) return true;
        if (from && ts < new Date(`${from}T00:00:00`).getTime()) return false;
        if (to && ts > new Date(`${to}T23:59:59`).getTime()) return false;
        return true;
      }),
    [logs, nameFilter, from, to]
  );

  const totalPaid = filtered.reduce((s, l) => s + (Number(l.total_owed) || 0), 0);

  return (
    <div>
      <div className="mb-4 grid gap-3 md:grid-cols-[1fr_170px_170px_190px]">
        <input
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          placeholder="Filter by employee…"
          className="w-full border border-edge bg-void px-3 py-2.5 font-mono text-sm text-snow outline-none placeholder:text-dim focus:border-neon focus:shadow-neon-teal"
        />
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="border border-edge bg-void px-3 py-2.5 font-mono text-sm text-snow outline-none [color-scheme:dark] focus:border-neon"
        />
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="border border-edge bg-void px-3 py-2.5 font-mono text-sm text-snow outline-none [color-scheme:dark] focus:border-neon"
        />
        <div className="flex items-center justify-between gap-2 border border-neon/40 bg-surface px-3 py-2.5">
          <span className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-dim">
            Total paid
          </span>
          <span className="tnum font-mono text-sm font-bold text-neon">
            {formatMoney(totalPaid)}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto border border-edge bg-panel">
        <table className="w-full min-w-[720px] border-collapse text-left">
          <thead>
            <tr className="border-b border-edge font-mono text-[10px] uppercase tracking-widest text-dim">
              <th className="px-4 py-3">Timestamp</th>
              <th className="px-4 py-3">Employee</th>
              <th className="px-4 py-3">Items sold</th>
              <th className="px-4 py-3 text-right">Total owed / paid</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center font-mono text-[11px] uppercase tracking-widest text-dim">
                  No shifts match the current filters
                </td>
              </tr>
            ) : (
              filtered.map((l) => (
                <tr key={l.id} className="border-b border-edge/60 last:border-0">
                  <td className="px-4 py-3 font-mono text-xs text-mist">
                    {formatTime(l.timestamp)}
                  </td>
                  <td className="px-4 py-3 font-mono text-sm font-bold text-snow">
                    {l.employee_name}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-mist">
                    {shiftBreakdown(l.items_sold_json)}
                  </td>
                  <td className="tnum px-4 py-3 text-right font-mono text-sm font-bold text-neon">
                    {formatMoney(l.total_owed)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Btn
                      variant="ember-outline"
                      size="xs"
                      disabled={busy === l.id}
                      onClick={() => handleDelete(l)}
                    >
                      {busy === l.id ? 'Removing…' : 'Remove'}
                    </Btn>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function AuditHistory() {
  const { snapshot, token, pushToast, refresh } = useApp();
  const guard = useRequireManager();
  const audits = snapshot.audits || [];
  const [busy, setBusy] = useState(null);

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete audit from ${a.employee_name} (${formatTime(a.timestamp)})? This only removes the log entry.`)) return;
    setBusy(a.id);
    try {
      await api.deleteAudit(a.id, token);
      pushToast('Audit log removed');
      refresh();
    } catch (err) {
      guard(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="overflow-x-auto border border-edge bg-panel">
      <table className="w-full min-w-[680px] border-collapse text-left">
        <thead>
          <tr className="border-b border-edge font-mono text-[10px] uppercase tracking-widest text-dim">
            <th className="px-4 py-3">Timestamp</th>
            <th className="px-4 py-3">Employee</th>
            <th className="px-4 py-3">Physical counts</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {audits.length === 0 ? (
            <tr>
              <td
                colSpan={4}
                className="px-4 py-8 text-center font-mono text-[11px] uppercase tracking-widest text-dim"
              >
                No inventory audits recorded yet
              </td>
            </tr>
          ) : (
            audits.map((a) => {
              const data = safeParse(a.audit_data_json, []);
              const summary = Array.isArray(data)
                ? data.map((d) => `${d.name} → ${d.count}`).join('   ')
                : '—';
              return (
                <tr key={a.id} className="border-b border-edge/60 last:border-0">
                  <td className="px-4 py-3 font-mono text-xs text-mist">
                    {formatTime(a.timestamp)}
                  </td>
                  <td className="px-4 py-3 font-mono text-sm font-bold text-snow">
                    {a.employee_name}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-mist">{summary}</td>
                  <td className="px-4 py-3 text-right">
                    <Btn
                      variant="ember-outline"
                      size="xs"
                      disabled={busy === a.id}
                      onClick={() => handleDelete(a)}
                    >
                      {busy === a.id ? 'Removing…' : 'Remove'}
                    </Btn>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}